import { useMemo } from 'react';
import { TCoreParticipation, TParticipation } from '../../ducks/api-ducks/participation-details-duck/types-participation-details';
import { useAppSelector } from '../../ducks/hooks';
import { ETabId } from '../../services/tab-configurations/tab-types';
import { TPartitionGroup, EPartitionCadence, TFilters } from '../../ducks/filters/types-filters';
import { useMonthAndDay, useMonthAndYear } from '../../utils/date-utils';
import { TPaymentPartition } from '../../ducks/api-ducks/earnings-duck/types-earnings';
import i18next from '../../i18n';
import { isNPM, useIsManager, useIsRBI } from '../../utils/participation-incentive-type-hook';
import { useIsBusinessImpersonating } from '../../utils/impersonation-utils';
import {
  getDefaultPartition,
  getDefaultPartitionGroup,
  getDefaultPaymentPartition,
  getManagerPartitionGroup,
  getPartition,
  getPaymentPartitionByParticipationPartition,
  getPaymentsPartitionGroup,
} from '../../ducks/filters/utils-filters';

export const useCoreParticipations = () => {
  const participationDetails = useAppSelector((state) => state.participationDetails);
  return participationDetails?.coreParticipations || [];
};

export const useAvailableFiscalYears = () => {
  const coreParticipations = useCoreParticipations();
  const isBusinessImpersonating = useIsBusinessImpersonating();
  const filteredParticipations = coreParticipations.filter(
    (participation) => participation.isPlanActive || isNPM(participation) || isBusinessImpersonating
  );
  const sortedParticipations = [...filteredParticipations].sort((a, b) => b.fiscalYearId - a.fiscalYearId);
  return useMemo(() => [...new Set(sortedParticipations.map((participation) => participation.fiscalYearId))], [sortedParticipations]);
};

export const useAvailableParticipations = (fiscalYear?: string): TCoreParticipation[] => {
  const coreParticipations = useCoreParticipations();
  const isBusinessImpersonating = useIsBusinessImpersonating();
  // Filter participations for the given fiscal year that are active, or are NPMs, or are impersonated
  return useMemo(
    () =>
      coreParticipations.filter(
        (participation) =>
          participation.fiscalYearId === Number(fiscalYear) && (participation.isPlanActive || isNPM(participation) || isBusinessImpersonating)
      ),
    [coreParticipations, fiscalYear, isBusinessImpersonating]
  );
};

export const useAvailableParticipationPartitionCadences = (selectedParticipationId: string, selectedPartitionGroup: string): TPartitionGroup[] => {
  const participationDetails = useAppSelector((state) => state.participationDetails);

  const coreParticipations = useMemo(() => participationDetails?.coreParticipations || [], [participationDetails]);
  return useMemo(() => {
    const selectedParticipation = coreParticipations.find(
      (participation) =>
        participation.participationId === Number(selectedParticipationId) && participation.fiscalYearId === Number(selectedPartitionGroup)
    );
    return selectedParticipation?.availablePartitions?.filter((partition) => partition.partitionCadence !== EPartitionCadence.Monthly) || [];
  }, [coreParticipations, selectedParticipationId, selectedPartitionGroup]);
};

export const useAvailablePerformancePartitionCadences = (selectedParticipationId: string, selectedPartitionGroup: string): TPartitionGroup[] => {
  const participationDetails = useAppSelector((state) => state.participationDetails);

  const coreParticipations = useMemo(() => participationDetails?.coreParticipations || [], [participationDetails]);
  return useMemo(() => {
    const selectedParticipation = coreParticipations.find(
      (participation) =>
        participation.participationId === Number(selectedParticipationId) && participation.fiscalYearId === Number(selectedPartitionGroup)
    );
    return selectedParticipation?.availablePartitions || [];
  }, [coreParticipations, selectedParticipationId, selectedPartitionGroup]);
};

export const useAvailableParticipationPartitions = (
  selectedParticipationId: string,
  selectedCadence: EPartitionCadence,
  selectedParticipationPartitionGroup: string
) => {
  const availableCadences = useAvailableParticipationPartitionCadences(selectedParticipationId, selectedParticipationPartitionGroup);
  return useMemo(() => {
    const selectedPartitionGroup = availableCadences.find((cadence) => cadence.partitionCadence === selectedCadence);
    if (!selectedPartitionGroup) return [];

    const { partitions } = selectedPartitionGroup;
    return [...partitions].sort((a, b) => a.order - b.order);
  }, [availableCadences, selectedCadence]);
};

export const useAvailablePerformancePartitions = (
  selectedParticipationId: string,
  selectedCadence: EPartitionCadence,
  selectedParticipationPartitionGroup: string
) => {
  const availableCadences = useAvailablePerformancePartitionCadences(selectedParticipationId, selectedParticipationPartitionGroup);
  return useMemo(() => {
    const selectedPartitionGroup = availableCadences.find((cadence) => cadence.partitionCadence === selectedCadence);
    if (!selectedPartitionGroup) return [];

    // Remove commas from all string fields in each partition
    const cleanPartitions = selectedPartitionGroup.partitions.map((partition) => {
      const cleaned: any = { ...partition };
      Object.keys(cleaned).forEach((key) => {
        if (typeof cleaned[key] === 'string') {
          cleaned[key] = cleaned[key].replace(',', '');
        }
      });
      return cleaned;
    });
    return cleanPartitions.sort((a, b) => a.order - b.order);
  }, [availableCadences, selectedCadence]);
};

export const useAvailableManagerPartitionGroups = (): string[] => {
  const participationDetails = useAppSelector((state) => state.participationDetails);
  return useMemo(() => {
    const availableManagerPartitions = new Set<string>();
    participationDetails?.availableManagerPartitions?.forEach((partitionGroup) => {
      availableManagerPartitions.add(partitionGroup.partitionGroup || '');
    });
    return [...availableManagerPartitions];
  }, [participationDetails]);
};

export const useAvailableManagerCadences = (selectedManagerPartitionGroupName: string): TPartitionGroup[] => {
  const participationDetails = useAppSelector((state) => state.participationDetails);
  return useMemo(() => {
    if (!participationDetails) return [];

    const { availableManagerPartitions } = participationDetails;
    return availableManagerPartitions?.filter((partition) => partition.partitionGroup === selectedManagerPartitionGroupName) || [];
  }, [participationDetails, selectedManagerPartitionGroupName]);
};

export const useAvailableManagerPartitions = (selectedPartitionGroupName: string, selectedCadence: EPartitionCadence) => {
  const availableCadences = useAvailableManagerCadences(selectedPartitionGroupName);
  return useMemo(() => {
    const selectedPartitionGroup = availableCadences.find((group) => group.partitionCadence === selectedCadence);
    if (!selectedPartitionGroup) return [];

    const { partitions } = selectedPartitionGroup;
    return [...partitions].sort((a, b) => a.order - b.order);
  }, [availableCadences, selectedCadence]);
};

const generatePaymentPartitionPillLabel = (monthAndDay: (date: Date | string) => string, partition: TPaymentPartition) => {
  let partitionLabel = `${partition.codePeriod} (${monthAndDay(partition.earningsStartDate)} - ${monthAndDay(partition.earningsEndDate)})`;
  // for true up payments we label them accordingly
  if (partition.isTrueUp) {
    partitionLabel = `${partitionLabel} ${i18next.t('filtersPanelTimePeriodLabelTrueUp')}`;
  }
  return partitionLabel;
};

// Filter Sync Functions
export const participationToManagerFilterSync = (filters: TFilters, participationDetails: TParticipation): TFilters => {
  const { selectedParticipationPartitionGroup, selectedParticipationPartition } = filters;
  const selectedManagerPartitionGroup = getManagerPartitionGroup(
    selectedParticipationPartitionGroup?.partitionGroup,
    selectedParticipationPartitionGroup?.partitionCadence,
    participationDetails
  );
  const availableManagerPartitions = selectedManagerPartitionGroup?.partitions;
  // If the selected manager partition groups contains a partition with the same title as the participation partition select it
  let selectedManagerPartition = availableManagerPartitions?.find((partition) => partition.title === selectedParticipationPartition?.title);

  // If no partition with the same title was found, use dates to pick the correct one
  // We pick the closest one to the selected participation partition
  if (!selectedManagerPartition) {
    selectedManagerPartition = availableManagerPartitions?.reduce((partitionToSelect, partition) => {
      const currentMinDiff = Math.abs(
        new Date(partitionToSelect.endDate).getTime() - new Date(selectedParticipationPartition?.endDate || '').getTime()
      );
      const curretnDiff = Math.abs(new Date(partition.endDate).getTime() - new Date(selectedParticipationPartition?.endDate || '').getTime());
      return curretnDiff < currentMinDiff ? partition : partitionToSelect;
    }, availableManagerPartitions[0]);
  }
  return {
    ...filters,
    selectedManagerPartitionGroup,
    selectedManagerPartition,
  };
};

// Syncs participation filters to performance filters so that performance filter values match participation filter values 1-to-1.
export const participationToPerformanceFilterSync = (filters: TFilters): TFilters => ({
  ...filters,
  selectedPerformancePartitionGroup: filters.selectedParticipationPartitionGroup,
  selectedPerformancePartition: filters.selectedParticipationPartition,
});

export const participationToPaymentFilterSync = (filters: TFilters): TFilters => {
  const { selectedParticipationPartition, selectedParticipationPartitionGroup, availablePaymentPartitions } = filters;
  const selectedPaymentPartitionGroup = getPaymentsPartitionGroup(
    selectedParticipationPartitionGroup?.partitionGroup || '', // ParticipationGroupName since we want to sync
    availablePaymentPartitions || []
  );
  let selectedPaymentPartition;
  if (selectedPaymentPartitionGroup) {
    selectedPaymentPartition = getPaymentPartitionByParticipationPartition(selectedPaymentPartitionGroup.partitions, selectedParticipationPartition);
  }
  if (!selectedPaymentPartition) {
    selectedPaymentPartition = getDefaultPaymentPartition(selectedPaymentPartitionGroup?.partitions || []);
  }
  return {
    ...filters,
    selectedPaymentPartitionGroup,
    selectedPaymentPartition,
  };
};

export const managerToParticipationFilterSync = (
  filters: TFilters,
  participationDetails: TParticipation,
  participationCadence: EPartitionCadence
): TFilters => {
  if (participationDetails === null || participationDetails === undefined || participationCadence === null || participationCadence === undefined) {
    // If participation details or cadence are not provided, we can't sync
    return filters;
  }
  const { participationId, selectedManagerPartition, selectedManagerPartitionGroup } = filters;
  const selectedParticipation = participationDetails.coreParticipations.find(
    (participation) =>
      participation.participationId === participationId && participation.fiscalYearId === Number(selectedManagerPartitionGroup?.partitionGroup)
  );
  // We keep the cadence of participation filters to select the possible partitions
  let selectedParticipationPartitionGroup = selectedParticipation?.availablePartitions?.find(
    (partition) => partition.partitionCadence === participationCadence
  );
  let selectedParticipationPartition;
  // We select the partition with the closes end date to the selected manager partition
  if (selectedParticipationPartitionGroup) {
    // We add the partitionGroup title to the selectedParticipationPartitionGroup
    selectedParticipationPartitionGroup = {
      ...selectedParticipationPartitionGroup,
      partitionGroup: selectedManagerPartitionGroup?.partitionGroup,
    };

    selectedParticipationPartition = selectedParticipationPartitionGroup.partitions.reduce((partitionToSelect, partition) => {
      const currentMinDiff = Math.abs(new Date(partitionToSelect.endDate).getTime() - new Date(selectedManagerPartition?.endDate || '').getTime());
      const currentDiff = Math.abs(new Date(partition.endDate).getTime() - new Date(selectedManagerPartition?.endDate || '').getTime());
      return currentDiff < currentMinDiff ? partition : partitionToSelect;
    }, selectedParticipationPartitionGroup.partitions[0]);
  }

  return {
    ...filters,
    selectedParticipationPartitionGroup,
    selectedParticipationPartition,
  };
};

export const managerToPerformanceFilterSync = (
  filters: TFilters,
  participationDetails: TParticipation,
  participationCadence: EPartitionCadence
): TFilters => {
  const { participationId, selectedManagerPartition, selectedManagerPartitionGroup } = filters;
  const selectedParticipation = participationDetails.coreParticipations.find(
    (participation) =>
      participation.participationId === participationId && participation.fiscalYearId === Number(selectedManagerPartitionGroup?.partitionGroup)
  );
  // We keep the cadence of participation filters to select the possible partitions
  let selectedPerformancePartitionGroup = selectedParticipation?.availablePartitions?.find(
    (partition) => partition.partitionCadence === participationCadence
  );
  let selectedPerformancePartition;
  // We select the partition with the closes end date to the selected manager partition
  if (selectedPerformancePartitionGroup) {
    // We add the partitionGroup title to the selectedPerformancePartitionGroup
    selectedPerformancePartitionGroup = {
      ...selectedPerformancePartitionGroup,
      partitionGroup: selectedManagerPartitionGroup?.partitionGroup,
    };

    selectedPerformancePartition = selectedPerformancePartitionGroup.partitions.reduce((partitionToSelect, partition) => {
      const currentMinDiff = Math.abs(new Date(partitionToSelect.endDate).getTime() - new Date(selectedManagerPartition?.endDate || '').getTime());
      const currentDiff = Math.abs(new Date(partition.endDate).getTime() - new Date(selectedManagerPartition?.endDate || '').getTime());
      return currentDiff < currentMinDiff ? partition : partitionToSelect;
    }, selectedPerformancePartitionGroup.partitions[0]);
  }

  return {
    ...filters,
    selectedPerformancePartitionGroup,
    selectedPerformancePartition,
  };
};

export const managerToPaymentFilterSync = (filters: TFilters): TFilters => {
  const { selectedManagerPartition, selectedManagerPartitionGroup, availablePaymentPartitions } = filters;
  const selectedPaymentPartitionGroup = getPaymentsPartitionGroup(
    selectedManagerPartitionGroup?.partitionGroup || '', // ParticipationGroupName since we want to sync
    availablePaymentPartitions || []
  );
  let selectedPaymentPartition;
  if (selectedPaymentPartitionGroup) {
    selectedPaymentPartition = getPaymentPartitionByParticipationPartition(selectedPaymentPartitionGroup.partitions, selectedManagerPartition);
  }
  if (!selectedPaymentPartition) {
    selectedPaymentPartition = getDefaultPaymentPartition(selectedPaymentPartitionGroup?.partitions || []);
  }
  return {
    ...filters,
    selectedPaymentPartitionGroup,
    selectedPaymentPartition,
  };
};

export const paymentToParticipationFilterSync = (filters: TFilters, participationDetails: TParticipation): TFilters => {
  const { participationId, selectedPaymentPartitionGroup, selectedPaymentPartition } = filters;
  const selectedParticipation = participationDetails.coreParticipations.find(
    (p) => p.participationId === participationId && p.fiscalYearId === Number(selectedPaymentPartitionGroup?.partitionGroup)
  );
  let selectedParticipationPartitionGroup = getDefaultPartitionGroup(
    selectedParticipation,
    selectedParticipation?.availablePartitions || [],
    Number(selectedPaymentPartitionGroup?.partitionGroup)
  );
  let selectedParticipationPartition;
  if (selectedParticipationPartitionGroup) {
    // We 'decorate' the selected PartitionGroup with the corresponding name (FY) since its not included
    // in the participation data
    selectedParticipationPartitionGroup = {
      ...selectedParticipationPartitionGroup,
      partitionGroup: selectedPaymentPartitionGroup?.partitionGroup,
    };
    if (selectedPaymentPartition) {
      // If the selected FY has a payment partition, sync Participation and Manager filters with it
      const selectedPaymentPartitionCodePeriod = selectedPaymentPartition.codePeriod;
      selectedParticipationPartition = selectedPaymentPartitionCodePeriod
        ? getPartition(selectedPaymentPartitionCodePeriod, selectedParticipationPartitionGroup)
        : getDefaultPartition(selectedParticipationPartitionGroup.partitions);
      // If no partition with the same name is found, use dates to pick the correct one
      // We pick the closest one to the selected payment partition end date
      if (!selectedParticipationPartition) {
        selectedParticipationPartition = selectedParticipationPartitionGroup.partitions.reduce((partitionToSelect, partition) => {
          const currentMinDiff = Math.abs(
            new Date(partitionToSelect.endDate).getTime() - new Date(selectedPaymentPartition.earningsEndDate).getTime()
          );
          const curretnDiff = Math.abs(new Date(partition.endDate).getTime() - new Date(selectedPaymentPartition.earningsEndDate).getTime());
          return curretnDiff < currentMinDiff ? partition : partitionToSelect;
        }, selectedParticipationPartitionGroup.partitions[0]);
      }
    } else {
      // If there are no payment documents for the selected fY, use defaults for Manager and Participation filters
      // Get default partition for participation
      selectedParticipationPartition = getDefaultPartition(selectedParticipationPartitionGroup.partitions);
    }
  }
  return {
    ...filters,
    selectedParticipationPartitionGroup,
    selectedParticipationPartition,
  };
};

export const paymentToPerformanceFilterSync = (filters: TFilters, participationDetails: TParticipation): TFilters => {
  const { participationId, selectedPaymentPartitionGroup, selectedPaymentPartition } = filters;
  const selectedParticipation = participationDetails.coreParticipations.find(
    (p) => p.participationId === participationId && p.fiscalYearId === Number(selectedPaymentPartitionGroup?.partitionGroup)
  );
  let selectedPerformancePartitionGroup = getDefaultPartitionGroup(
    selectedParticipation,
    selectedParticipation?.availablePartitions || [],
    Number(selectedPaymentPartitionGroup?.partitionGroup)
  );
  let selectedPerformancePartition;
  if (selectedPerformancePartitionGroup) {
    // We 'decorate' the selected PartitionGroup with the corresponding name (FY) since its not included
    // in the participation data
    selectedPerformancePartitionGroup = {
      ...selectedPerformancePartitionGroup,
      partitionGroup: selectedPaymentPartitionGroup?.partitionGroup,
    };
    if (selectedPaymentPartition) {
      // If the selected FY has a payment partition, sync Participation and Manager filters with it
      const selectedPaymentPartitionCodePeriod = selectedPaymentPartition.codePeriod;
      selectedPerformancePartition = selectedPaymentPartitionCodePeriod
        ? getPartition(selectedPaymentPartitionCodePeriod, selectedPerformancePartitionGroup)
        : getDefaultPartition(selectedPerformancePartitionGroup.partitions);
      // If no partition with the same name is found, use dates to pick the correct one
      // We pick the closest one to the selected payment partition end date
      if (!selectedPerformancePartition) {
        selectedPerformancePartition = selectedPerformancePartitionGroup.partitions.reduce((partitionToSelect, partition) => {
          const currentMinDiff = Math.abs(
            new Date(partitionToSelect.endDate).getTime() - new Date(selectedPaymentPartition.earningsEndDate).getTime()
          );
          const currentDiff = Math.abs(new Date(partition.endDate).getTime() - new Date(selectedPaymentPartition.earningsEndDate).getTime());
          return currentDiff < currentMinDiff ? partition : partitionToSelect;
        }, selectedPerformancePartitionGroup.partitions[0]);
      }
    } else {
      // If there are no payment documents for the selected fY, use defaults for Manager and Participation filters
      // Get default partition for participation
      selectedPerformancePartition = getDefaultPartition(selectedPerformancePartitionGroup.partitions);
    }
  }
  return {
    ...filters,
    selectedPerformancePartitionGroup,
    selectedPerformancePartition,
  };
};

export const paymentToManagerFilterSync = (filters: TFilters, participationDetails: TParticipation): TFilters => {
  const { selectedPaymentPartitionGroup, selectedPaymentPartition } = filters;
  const { availableManagerPartitions } = participationDetails;
  const selectedManagerPartitionGroup = availableManagerPartitions?.find(
    (group) => group.partitionGroup === selectedPaymentPartitionGroup?.partitionGroup
  );
  if (!selectedManagerPartitionGroup) return filters;
  const selectedManagerPartition = selectedManagerPartitionGroup.partitions.find(
    (partition) => partition.title === selectedPaymentPartition?.codePeriod
  );
  return {
    ...filters,
    selectedManagerPartitionGroup,
    selectedManagerPartition,
  };
};

// Syncs performance filters to participation filters. If performance cadence is Monthly, participation falls back to Quarterly or Semester and selects the containing range.
export const performanceToParticipationFilterSync = (filters: TFilters, participationDetails: TParticipation): TFilters => {
  const { selectedPerformancePartitionGroup, selectedPerformancePartition, participationId } = filters;
  // If not Monthly, just sync 1-to-1
  if (selectedPerformancePartitionGroup?.partitionCadence !== EPartitionCadence.Monthly) {
    return {
      ...filters,
      selectedParticipationPartitionGroup: selectedPerformancePartitionGroup,
      selectedParticipationPartition: selectedPerformancePartition,
    };
  }
  // If Monthly, find the containing Quarterly or Semester partition
  const participation = participationDetails.coreParticipations.find(
    (p) => p.participationId === participationId && p.fiscalYearId === Number(selectedPerformancePartitionGroup?.partitionGroup)
  );
  if (!participation) return filters;
  // Try Quarterly first, then SemiAnnual
  const fallbackCadences = [EPartitionCadence.Quarterly, EPartitionCadence.SemiAnnual];
  const containingPartitionGroup = fallbackCadences
    .map((cadence) => participation.availablePartitions?.find((pg) => pg.partitionCadence === cadence))
    .find((group) => group !== undefined);
  if (!containingPartitionGroup) return filters;
  // Find the range that contains the selected month
  const containingPartition = containingPartitionGroup.partitions.find((partition) => {
    if (!selectedPerformancePartition) return false;
    const monthStart = new Date(selectedPerformancePartition.startDate);
    const monthEnd = new Date(selectedPerformancePartition.endDate);
    const rangeStart = new Date(partition.startDate);
    const rangeEnd = new Date(partition.endDate);
    return monthStart >= rangeStart && monthEnd <= rangeEnd;
  });
  return {
    ...filters,
    selectedParticipationPartitionGroup: {
      ...containingPartitionGroup,
      partitionGroup: selectedPerformancePartitionGroup?.partitionGroup,
    },
    selectedParticipationPartition: containingPartition || containingPartitionGroup.partitions[0],
  };
};

export const performanceToManagerFilterSync = (filters: TFilters, participationDetails: TParticipation): TFilters => {
  const { selectedPerformancePartitionGroup, selectedPerformancePartition } = filters;
  const selectedManagerPartitionGroup = getManagerPartitionGroup(
    selectedPerformancePartitionGroup?.partitionGroup,
    selectedPerformancePartitionGroup?.partitionCadence,
    participationDetails
  );
  const availableManagerPartitions = selectedManagerPartitionGroup?.partitions;
  // If the selected manager partition groups contains a partition with the same title as the participation partition select it
  let selectedManagerPartition = availableManagerPartitions?.find((partition) => partition.title === selectedPerformancePartition?.title);

  // If no partition with the same title was found, use dates to pick the correct one
  // We pick the closest one to the selected participation partition
  if (!selectedManagerPartition) {
    selectedManagerPartition = availableManagerPartitions?.reduce((partitionToSelect, partition) => {
      const currentMinDiff = Math.abs(
        new Date(partitionToSelect.endDate).getTime() - new Date(selectedPerformancePartition?.endDate || '').getTime()
      );
      const currentDiff = Math.abs(new Date(partition.endDate).getTime() - new Date(selectedPerformancePartition?.endDate || '').getTime());
      return currentDiff < currentMinDiff ? partition : partitionToSelect;
    }, availableManagerPartitions[0]);
  }
  return {
    ...filters,
    selectedManagerPartitionGroup,
    selectedManagerPartition,
  };
};

export const performanceToPaymentFilterSync = (filters: TFilters): TFilters => {
  const { selectedPerformancePartition, selectedPerformancePartitionGroup, availablePaymentPartitions } = filters;
  const selectedPaymentPartitionGroup = getPaymentsPartitionGroup(
    selectedPerformancePartitionGroup?.partitionGroup || '', // ParticipationGroupName since we want to sync
    availablePaymentPartitions || []
  );
  let selectedPaymentPartition;
  if (selectedPaymentPartitionGroup) {
    selectedPaymentPartition = getPaymentPartitionByParticipationPartition(selectedPaymentPartitionGroup.partitions, selectedPerformancePartition);
  }
  if (!selectedPaymentPartition) {
    selectedPaymentPartition = getDefaultPaymentPartition(selectedPaymentPartitionGroup?.partitions || []);
  }
  return {
    ...filters,
    selectedPaymentPartitionGroup,
    selectedPaymentPartition,
  };
};

// Filter Pills dynamic filter selectors
export const useTermSelection = () => {
  const isRBI = useIsRBI();
  const monthAndDay = useMonthAndDay();
  const filters = useAppSelector((state) => state.filters);
  const showMonthlyPage = isRBI && filters?.selectedPerformancePartitionGroup?.partitionCadence === EPartitionCadence.Monthly;

  return useMemo(() => {
    switch (filters.activeTab) {
      case ETabId.PerformanceTab:
        return showMonthlyPage ? i18next.t('pagePerformanceMonthlyPill') : filters.selectedParticipationPartition?.title;
      case ETabId.ComplianceTab:
      case ETabId.PlanSummaryTab:
      case ETabId.MyChangeHistoryTab:
      case ETabId.EstimatorTab:
        return filters.selectedParticipationPartition?.title;
      case ETabId.MyTeamTab:
        return filters.selectedManagerPartition?.title;
      case ETabId.EarningsTab:
        return filters.selectedPaymentPartition ? generatePaymentPartitionPillLabel(monthAndDay, filters.selectedPaymentPartition) : '';
      default:
        return null;
    }
  }, [
    monthAndDay,
    showMonthlyPage,
    filters.activeTab,
    filters.selectedManagerPartition?.title,
    filters.selectedParticipationPartition?.title,
    filters.selectedPaymentPartition,
  ]);
};

export const useFiscalYearSelection = () => {
  const filters = useAppSelector((state) => state.filters);

  return useMemo(() => {
    switch (filters.activeTab) {
      case ETabId.ComplianceTab:
      case ETabId.PerformanceTab:
      case ETabId.PlanSummaryTab:
      case ETabId.EarningsTab:
      case ETabId.MyChangeHistoryTab:
      case ETabId.EstimatorTab:
        return filters.selectedParticipationPartitionGroup?.partitionGroup;
      case ETabId.MyTeamTab:
        return filters.selectedManagerPartitionGroup?.partitionGroup;
      default:
        return null;
    }
  }, [filters.activeTab, filters.selectedManagerPartitionGroup?.partitionGroup, filters.selectedParticipationPartitionGroup?.partitionGroup]);
};

export const useRoleSelection = () => {
  const monthAndYear = useMonthAndYear();
  const filters = useAppSelector((state) => state.filters);

  const fiscalYear = useFiscalYearSelection();
  const availableParticipations = useAvailableParticipations(`${fiscalYear}`);

  return useMemo(() => {
    const participation = availableParticipations.find((elm) => elm.participationId === filters.participationId) || availableParticipations[0];
    let pName: string = participation ? `${participation.roleSummary}` : '';
    if (participation && participation.participationId !== 0) {
      pName += ` (${monthAndYear(participation.participationStartDateTime)})`;
    }
    // We need to get all the roles available for the selected payment period./
    // First we filter all available participations (current FY) which dates falls within the selected payment period.
    const paymentPeriodRoles = filters.selectedPaymentPartition?.roleSummary;

    switch (filters.activeTab) {
      case ETabId.ComplianceTab:
      case ETabId.PerformanceTab:
      case ETabId.PlanSummaryTab:
      case ETabId.MyTeamTab:
      case ETabId.MyChangeHistoryTab:
      case ETabId.EstimatorTab:
        return pName;
      case ETabId.EarningsTab:
        return paymentPeriodRoles;
      default:
        return null;
    }
  }, [monthAndYear, availableParticipations, filters.activeTab, filters.participationId, filters.selectedPaymentPartition?.roleSummary]);
};

// Filter component guards
// Participation filters are the default filters.
export const useShowParticipationFilters = () => {
  const { activeTab } = useAppSelector((state) => state.filters);

  return useMemo(() => {
    switch (activeTab) {
      case ETabId.PlanSummaryTab:
      case ETabId.ComplianceTab:
      case ETabId.MyChangeHistoryTab:
      case ETabId.EstimatorTab:
        return true;
      case ETabId.PerformanceTab:
        return false;
      default:
        return false;
    }
  }, [activeTab]);
};

// Show Monthly Performance Filters when selected Tab is Performance
export const useShowPerformanceFilters = () => {
  const { activeTab } = useAppSelector((state) => state.filters);

  return useMemo(() => {
    switch (activeTab) {
      case ETabId.PerformanceTab:
        return true;
      default:
        return false;
    }
  }, [activeTab]);
};

// Show Manager Filters when selected Tab is My Team Tab OR there is no selected tab and user is an NPM
export const useShowManagerFilters = () => {
  const { activeTab } = useAppSelector((state) => state.filters);
  const isManager = useIsManager();

  return useMemo(() => {
    switch (activeTab) {
      case ETabId.MyTeamTab:
        return true;
      case undefined:
        return isManager;
      default:
        return false;
    }
  }, [activeTab, isManager]);
};

export const useShowEarningsFilters = () => {
  const { activeTab } = useAppSelector((state) => state.filters);

  return useMemo(() => {
    switch (activeTab) {
      case ETabId.EarningsTab:
        return true;
      default:
        return false;
    }
  }, [activeTab]);
};
